import { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Modal } from 'antd';
import dayjs from 'dayjs';
import { useTaskDetailStore } from './details_store';
import { WorkReponse } from '../../../model/work_response';
import { ApiError } from '../../../api/api';
import { Messages } from '../../../constant/messages';
import { useIndicator } from '../../../../src/hook/use_indicator';
import { SectionIcon } from '../section_icon/section_icon';
import { DataSourceMap, DataTypeCode, Sections } from '../../../constant';

import styles from './index.module.scss';
import { createDetails } from './task_details_map';

export type WorkApi = {
  show: (workId: number, planId: number) => void;
};

/**
 * 作業詳細情報取得用Hook
 * @returns {[WorkApi, React.ReactElement]} 作業詳細情報取得関数とモーダル要素
 */
export function useWorkDetails(): readonly [WorkApi, React.ReactElement] {
  /**
   * 作業詳細取得
   */
  const { getTaskDetails } = useTaskDetailStore((selector) => selector);

  /**
   * モーダル表示状態管理
   */
  const [showModal, setShowModal] = useState(false);
  const [data, setData] = useState<WorkReponse | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const indicator = useIndicator();
  const navigator = useNavigate();

  /**
   * 作業詳細モーダル表示処理
   * @param workId
   * @param planId
   */
  const show = (workId: number, planId: number) => {
    console.log('workId', workId);
    console.log('planId', planId);
    indicator.show();
    getTaskDetails(workId, planId).then((res) => {
      setData(res);
      setShowModal(true);
    }).catch((e) => {
      if (e instanceof ApiError) {
        const { status } = e.response;
        if (status === 404 || status === 400 || status === 500) {
          window.alert(Messages.ERR_DIALOG_RETRY_OPERATION);
        }
        if (status === -1) {
          window.alert(Messages.ERR_DIALOG_TIMEOUT);
        }
        if (status === 401 || status === 403) {
          window.alert(Messages.ERR_DIALOG_RETURN_LOGIN);
          navigator('/login', { state: { message: Messages.ERR_401 } });
        }
      }
    }).finally(() => {
      indicator.hide();
    });
  };

  useEffect(() => {
    if (ref.current && showModal) {
      setTimeout(() => {
        ref.current.scrollTop = 0;
      }, 100);
    }
  }, [showModal]);

  const edit = (workId: number, planId: number) => {
    return () => {
      navigator(`/issue/${workId}/details/${planId}`);
      setShowModal(false);
    };
  };

  const ls: CSSProperties = {
    width: '150px',
  };

  const item = (key: string, label: string, value: React.ReactNode, className: string = undefined, childClass: string = undefined) => {
    return (
        <tr key={key}>
          <th style={ls} className={className}>{label}</th>
          <td className={childClass}>{value}</td>
        </tr>
    );
  };

  const items = useMemo(() => {
    if (data === null) {
      return null;
    }
    const details = createDetails(data.details, data.dataType);
    const getDataSource = (source: string) => {
      if (DataSourceMap[source]) {
        return DataSourceMap[source];
      }
      return '不明';
    };

    return (
        <div className={styles.description}>
          <table>
            <tbody>
            {item('id', 'ID', data.id)}
            {item('planId', '計画ID', data.planId)}
            {item('division', '街区', data.division)}
            {item('floor', '階', data.floor)}
            {item('block', 'エリア', data.block)}
            {item('company', '会社名', data.companyName)}
            {item('target', '対象箇所', data.target)}
            {item('content', '作業内容', data.content)}
            {item('dataInputSource', 'データ投入元', getDataSource(data.dataInputSource))}
            {item('workType', '作業区分', data.sections.map((s) => {
              return <div key={s} className={styles.sections}>
                <SectionIcon sectionCode={s}/>
                {Sections.find((d) => d.key === s)?.value ?? ''}
              </div>;
            }))}
            {item('start', '開始', dayjs(data.plan.start).format('YYYY/MM/DD HH:mm'))}
            {item('end', '終了', dayjs(data.plan.end).format('YYYY/MM/DD HH:mm'))}
            <tr>
              <th colSpan={2} className={styles.detail}>帳票・データの個別情報</th>
            </tr>
            {details.map((d) => {
              if (d.span) {
                return (
                    <tr key={d.key}>
                      <th colSpan={2} className={styles.detail}>{d.name}</th>
                    </tr>
                );
              }
              return item(d.key, d.name, d.value, styles.detail, d.child ? styles.child : undefined);
            })}
            </tbody>
          </table>
        </div>
    );
  }, [data]);


  const element = useMemo(() => {
    const editButton = (() => {
      if (data?.dataType !== DataTypeCode.request) {
        return <span></span>;
      }
      return <Button type={'primary'} onClick={edit(data?.id, data?.planId)}>臨時作業入力画面で詳細を見る</Button>;
    })();

    return (
        <Modal
            zIndex={9500}
            title="詳細"
            open={showModal}
            closable={false}
            cancelText={'閉じる'}
            onOk={() => setShowModal(false)}
            onCancel={() => setShowModal(false)}
            footer={
              <div className={styles.footer}>
                {editButton}
                <Button key={'0'} onClick={() => setShowModal(false)}>閉じる</Button>
              </div>
            }
        >
          <div ref={ref} className={styles.modalContent}>
            {items}
          </div>
        </Modal>
    );
  }, [showModal]);

  return [{ show }, element];
}
